import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useProjectAccess } from '../contexts/ProjectAccessContext';
import { useToast } from '../contexts/ToastContext';
import { API_BASE } from '../config';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ProjectPanelLayout } from './ProjectPanelLayout';
import { ArrowUpDown, ArrowUp, ArrowDown, EllipsisVertical, X, FileType } from 'lucide-react';
import { RotatingDeleteIcon } from './RotatingDeleteIcon';
import { CONCEPT_ICONS, PROJECT_SECTION_ICONS } from '../utils/concept-icons';
import { ResourcesSearchInput } from './ResourcesSearchInput';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { AddResourceMenu } from './AddResourceMenu';
import { ResourcesEmptyState } from './ResourcesEmptyState';
import { useIsMobile } from '../hooks/use-mobile';
import { useShiftClickRowSelection } from '../hooks/useShiftClickRowSelection';
import { shouldShowHeaderCreateActions } from '../utils/header-create-actions';
import { ClearFiltersButton } from './ClearFiltersButton';
import { MultiSelectFilterDropdown } from './MultiSelectFilterDropdown';
import { ResourcesFilterMenu, type ResourcesFilterMenuEntry } from './ResourcesFilterMenu';
import { ResourcesSortMenu, type ResourcesSortOption } from './ResourcesSortMenu';
import { ConfirmDialog } from './ConfirmDialog';
import { UploadResourcesModal } from './UploadResourcesModal';
import { AddLinkModal } from './AddLinkModal';
import { AddGenericResourceModal } from './AddGenericResourceModal';
import { isSystemTag } from '../utils/system-tags';
import { EntityTagBadge } from './EntityTagsField';
import { StringTagsCell } from './StringTagsCell';
import { getResourceKind, RESOURCE_KIND_ICONS, type ResourceEntityTag } from '../utils/resource-kind';
import { getUrlHostname } from '../utils/resource-display-utils';
import { Tabs } from './ui/tabs';
import { ViewSwitcher, useViewModeSearchParam } from './ViewSwitcher';
import { ResourcesGrid } from './ResourcesGrid';
import { handleModifierAwareNavigate, navigationClickHandlers } from '../utils/app-navigation';
import { cn } from '../lib/utils';
import {
  applyLinkedEntityAssignment,
  applyTagAssignment,
  diffIds,
  intersectIds,
} from '../utils/resource-bulk-assign';
import {
  linkedResourceEntityIds,
  mergeTagNames,
  nonSystemTagsFromResources,
  presentResourceKindIds,
  resourceFilterOptions,
} from '../utils/resource-filter-options';
import {
  parseResourceListExcludedTagsFromSearchParams,
  parseResourceListTagList,
  parseResourceListTagsFromSearchParams,
} from '../utils/resource-list-navigation';
import { DataTableRowCheckbox } from './DataTableRowCheckbox';
import { DataTableSelectAllCheckbox } from './DataTableSelectAllCheckbox';
import { DataTableSelectionActions } from './DataTableSelectionActions';

interface EntityOption {
  id: string;
  name: string;
  documentId?: string;
}

const normalizeTagValue = (tag: string): string => tag.trim().toLowerCase();

function formatCreatedDate(iso: string): string {
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
}

function formatReferenceDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}

const EntityTagBadges: React.FC<{ entityTags: ResourceEntityTag[] }> = ({ entityTags }) => (
  <>
    {entityTags.map((entityTag) => (
      <EntityTagBadge
        key={`${entityTag.type}-${entityTag.id}`}
        entityType={entityTag.type}
        id={entityTag.id}
        name={entityTag.name}
        documentId={entityTag.documentId}
        className="gap-1 px-2 py-0.5 text-xs"
        maxLabelLength={15}
      />
    ))}
  </>
);

const TagsCell: React.FC<{
  tags: string[];
  entityTags: ResourceEntityTag[];
  availableTags: string[];
  onTagsChange: (tags: string[]) => Promise<void>;
  readOnly?: boolean;
}> = ({ tags, entityTags, availableTags, onTagsChange, readOnly = false }) => {
  if (readOnly && entityTags.length === 0) {
    return (
      <StringTagsCell
        tags={tags}
        availableTags={availableTags}
        onTagsChange={onTagsChange}
        readOnly
      />
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 w-full">
      <EntityTagBadges entityTags={entityTags} />
      <StringTagsCell
        tags={tags}
        availableTags={availableTags}
        onTagsChange={onTagsChange}
        readOnly={readOnly}
        deferEditor={!readOnly}
        emptyLabel={entityTags.length === 0 ? '-' : null}
      />
    </div>
  );
};

interface ResourceItem {
  id: string;
  fileName: string;
  title: string;
  fileSize: number | null;
  mimeType: string | null;
  s3Key: string | null;
  url: string | null;
  naturalId: string | null;
  resourceDate: string | null;
  notes: string | null;
  tags: string[];
  documentId: string | null;
  documentTitle: string | null;
  wordCount: number | null;
  createdAt: string;
  updatedAt: string;
}

export const ResourcesTable: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const { t } = useLocale();
  const { showToast, showErrorToast, showFieldSavedToast } = useToast();
  const { canEditProject } = useProjectAccess();
  const isMobile = useIsMobile();
  const [resources, setResources] = useState<ResourceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>(() => parseResourceListTagsFromSearchParams(searchParams));
  const [excludedTags, setExcludedTags] = useState<string[]>(() =>
    parseResourceListExcludedTagsFromSearchParams(searchParams)
  );
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isAddLinkModalOpen, setIsAddLinkModalOpen] = useState(false);
  const [isAddGenericResourceModalOpen, setIsAddGenericResourceModalOpen] = useState(false);
  const [characters, setCharacters] = useState<EntityOption[]>([]);
  const [locations, setLocations] = useState<EntityOption[]>([]);
  const [projectTags, setProjectTags] = useState<string[]>([]);
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [selectedResourceTypes, setSelectedResourceTypes] = useState<string[]>([]);
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
  const [searchInputResetKey, setSearchInputResetKey] = useState(0);
  const fetchAbortRef = React.useRef<AbortController | null>(null);
  const loadedProjectIdRef = React.useRef<string | null>(null);
  const [searchProjectId, setSearchProjectId] = useState(projectId);
  if (projectId !== searchProjectId) {
    setSearchProjectId(projectId);
    setAppliedSearchQuery('');
    loadedProjectIdRef.current = null;
  }

  const handleDebouncedSearch = React.useCallback((value: string): void => {
    setAppliedSearchQuery(value);
  }, []);
  const [viewMode, setViewMode] = useViewModeSearchParam({
    allowedModes: ['table', 'grid'] as const,
    defaultMode: 'table',
  });
  const [sortOption, setSortOption] = useState<ResourcesSortOption>('created_desc');
  const getVisibleRowIdsRef = React.useRef<() => string[]>(() => []);
  const {
    rowSelection,
    setRowSelection,
    selectedIds: selectedResourceIds,
    selectRangeTo,
    toggleRowSelected: toggleResourceSelected,
    clearSelection,
    setAnchorRowId,
    handleRowClick,
  } = useShiftClickRowSelection({
    getVisibleRowIds: () => getVisibleRowIdsRef.current(),
  });
  const resourcesRef = React.useRef<ResourceItem[]>([]);
  const entityRefsRef = React.useRef<Array<{
    sourceType: string;
    sourceId: string;
    targetType: string;
    targetId: string;
  }>>([]);
  const assignQueueRef = React.useRef(Promise.resolve());

  const tagsQuery = useMemo(() => {
    if (selectedTags.length === 0) {
      return undefined;
    }
    return selectedTags.join(',');
  }, [selectedTags]);

  const excludeTagsQuery = useMemo(() => {
    if (excludedTags.length === 0) {
      return undefined;
    }
    return excludedTags.join(',');
  }, [excludedTags]);

  const characterIdsQuery = useMemo(() => {
    if (selectedCharacterIds.length === 0) {
      return undefined;
    }
    return selectedCharacterIds.join(',');
  }, [selectedCharacterIds]);

  const locationIdsQuery = useMemo(() => {
    if (selectedLocationIds.length === 0) {
      return undefined;
    }
    return selectedLocationIds.join(',');
  }, [selectedLocationIds]);

  const tagsParam = searchParams.get('tags');
  const excludeTagsParam = searchParams.get('excludeTags');

  useEffect(() => {
    setSelectedTags(parseResourceListTagList(tagsParam));
    setExcludedTags(parseResourceListTagList(excludeTagsParam));
  }, [tagsParam, excludeTagsParam]);

  useEffect(() => {
    if (!projectId || !token) {
      return;
    }
    fetch(`${API_BASE}/api/projects/${projectId}/mentionable-entities`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const chars: EntityOption[] = (data.characters || []).map(
          (c: { id: string; name: string; documentId?: string }) => ({
            id: c.id,
            name: c.name,
            documentId: c.documentId,
          })
        );
        const locs: EntityOption[] = (data.locations || []).map(
          (l: { id: string; name: string; documentId?: string }) => ({
            id: l.id,
            name: l.name,
            documentId: l.documentId,
          })
        );
        const uniqueChars = Array.from(
          new Map(chars.map((c) => [c.id, c])).values()
        ).sort((a, b) => a.name.localeCompare(b.name));
        const uniqueLocs = Array.from(
          new Map(locs.map((l) => [l.id, l])).values()
        ).sort((a, b) => a.name.localeCompare(b.name));
        setCharacters(uniqueChars);
        setLocations(uniqueLocs);
      })
      .catch((err) => console.error('Error fetching mentionable entities:', err));
  }, [projectId, token]);

  useEffect(() => {
    if (!projectId || !token) {
      return;
    }
    fetch(`${API_BASE}/api/projects/${projectId}/tags`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setProjectTags(Array.isArray(data.tags) ? data.tags : []);
      })
      .catch((err) => console.error('Error fetching project tags:', err));
  }, [projectId, token]);

  const [entityRefs, setEntityRefs] = useState<Array<{
    sourceType: string;
    sourceId: string;
    targetType: string;
    targetId: string;
  }>>([]);

  const fetchEntityRefs = React.useCallback(async (): Promise<void> => {
    if (!projectId || !token) {
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/entity-references`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setEntityRefs(data.references ?? []);
    } catch (err) {
      console.error('Error fetching entity references:', err);
    }
  }, [projectId, token]);

  useEffect(() => {
    void fetchEntityRefs();
  }, [fetchEntityRefs]);

  const resourceEntityTags = useMemo(() => {
    const charactersById = new Map(characters.map((c) => [c.id, c]));
    const locationsById = new Map(locations.map((l) => [l.id, l]));
    const map = new Map<string, ResourceEntityTag[]>();
    for (const ref of entityRefs) {
      if (ref.sourceType !== 'resource') {
        continue;
      }
      let entity: EntityOption | undefined;
      let type: 'character' | 'location' | undefined;
      if (ref.targetType === 'character') {
        entity = charactersById.get(ref.targetId);
        type = 'character';
      } else if (ref.targetType === 'location') {
        entity = locationsById.get(ref.targetId);
        type = 'location';
      }
      if (!entity || !type) {
        continue;
      }
      const existing = map.get(ref.sourceId) ?? [];
      existing.push({ id: entity.id, name: entity.name, type, documentId: entity.documentId });
      map.set(ref.sourceId, existing);
    }
    return map;
  }, [entityRefs, characters, locations]);

  React.useEffect(() => {
    resourcesRef.current = resources;
  }, [resources]);

  React.useEffect(() => {
    entityRefsRef.current = entityRefs;
  }, [entityRefs]);

  const normalizeTag = React.useCallback((tag: string): string => {
    return normalizeTagValue(tag);
  }, []);

  const availableTags = useMemo(() => {
    const tagsOnResources = nonSystemTagsFromResources(resources);
    return mergeTagNames(projectTags, tagsOnResources, selectedTags, excludedTags);
  }, [projectTags, resources, selectedTags, excludedTags]);

  const fetchResources = React.useCallback(async ({
    searchQuery,
    tagsQuery,
    excludeTagsQuery,
    characterIds,
    locationIds,
    isInitial = false,
  }: {
    searchQuery?: string;
    tagsQuery?: string;
    excludeTagsQuery?: string;
    characterIds?: string;
    locationIds?: string;
    isInitial?: boolean;
  }): Promise<void> => {
    if (!projectId || !token) {
      return;
    }

    fetchAbortRef.current?.abort();
    const abortController = new AbortController();
    fetchAbortRef.current = abortController;

    try {
      if (isInitial) {
        setInitialLoading(true);
      } else {
        setLoading(true);
      }
      const url = new URL(`${API_BASE}/api/projects/${projectId}/resources`);
      if (searchQuery && searchQuery.trim()) {
        url.searchParams.set('search', searchQuery.trim());
      }
      if (tagsQuery && tagsQuery.trim()) {
        url.searchParams.set('tags', tagsQuery.trim());
      }
      if (excludeTagsQuery && excludeTagsQuery.trim()) {
        url.searchParams.set('excludeTags', excludeTagsQuery.trim());
      }
      if (characterIds && characterIds.trim()) {
        url.searchParams.set('characterIds', characterIds.trim());
      }
      if (locationIds && locationIds.trim()) {
        url.searchParams.set('locationIds', locationIds.trim());
      }
      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: abortController.signal,
      });

      if (abortController.signal.aborted) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setResources(data.resources || []);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('Error fetching resources:', error);
    } finally {
      if (fetchAbortRef.current === abortController) {
        if (isInitial) {
          setInitialLoading(false);
        } else {
          setLoading(false);
        }
      }
    }
  }, [projectId, token]);

  React.useEffect(() => {
    return () => {
      fetchAbortRef.current?.abort();
    };
  }, []);

  React.useEffect(() => {
    if (!projectId || !token) {
      return;
    }
    const isInitial = loadedProjectIdRef.current !== projectId;
    void fetchResources({
      searchQuery: appliedSearchQuery || undefined,
      tagsQuery,
      excludeTagsQuery,
      characterIds: characterIdsQuery,
      locationIds: locationIdsQuery,
      isInitial,
    }).finally(() => {
      loadedProjectIdRef.current = projectId;
    });
  }, [projectId, token, appliedSearchQuery, tagsQuery, excludeTagsQuery, characterIdsQuery, locationIdsQuery, fetchResources]);

  React.useEffect(() => {
    const handleResourceUploaded = (event: Event): void => {
      fetchResources({
        searchQuery: appliedSearchQuery || undefined,
        tagsQuery,
        excludeTagsQuery,
        characterIds: characterIdsQuery,
        locationIds: locationIdsQuery,
      });
      const detail = (event as CustomEvent<{ resourceIds?: string[]; projectId?: string }>).detail;
      if (detail?.resourceIds?.length && detail?.projectId && projectId === detail.projectId) {
        const resourceId = detail.resourceIds[detail.resourceIds.length - 1];
        navigate(`/projects/${detail.projectId}/resources/${resourceId}`);
      }
    };

    window.addEventListener('resource-uploaded', handleResourceUploaded);

    return () => {
      window.removeEventListener('resource-uploaded', handleResourceUploaded);
    };
  }, [fetchResources, appliedSearchQuery, tagsQuery, excludeTagsQuery, characterIdsQuery, locationIdsQuery, projectId, navigate]);

  const [pendingDeleteResourceIds, setPendingDeleteResourceIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const requestDeleteResource = React.useCallback((resourceId: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    setPendingDeleteResourceIds([resourceId]);
  }, []);

  const requestDeleteSelectedResources = React.useCallback((): void => {
    if (selectedResourceIds.length === 0) {
      return;
    }
    setPendingDeleteResourceIds([...selectedResourceIds]);
  }, [selectedResourceIds]);

  const confirmDeleteResources = React.useCallback(async (): Promise<void> => {
    const resourceIds = pendingDeleteResourceIds;
    setPendingDeleteResourceIds([]);
    if (resourceIds.length === 0 || !projectId || !token) {
      return;
    }

    setBulkDeleting(true);
    try {
      const results = await Promise.allSettled(
        resourceIds.map(async (resourceId) => {
          const response = await fetch(`${API_BASE}/api/projects/${projectId}/resources/${resourceId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error((error as { error?: string }).error || t('Failed to delete resource'));
          }
        })
      );
      const failed = results.filter((result) => result.status === 'rejected').length;
      const deleted = resourceIds.length - failed;
      if (deleted > 0) {
        clearSelection();
        fetchResources({
          searchQuery: appliedSearchQuery || undefined,
          tagsQuery,
          excludeTagsQuery,
          characterIds: characterIdsQuery,
          locationIds: locationIdsQuery,
        });
        showToast(
          deleted === 1
            ? t('resource_deleted_success')
            : t('resources_deleted_success').replace('{count}', String(deleted))
        );
      }
      if (failed > 0) {
        showErrorToast(
          t('resources_delete_partial_failed').replace('{count}', String(failed))
        );
      }
    } catch (error) {
      console.error('Error deleting resources:', error);
      showErrorToast(t('Failed to delete resource'));
    } finally {
      setBulkDeleting(false);
    }
  }, [
    pendingDeleteResourceIds,
    projectId,
    token,
    t,
    clearSelection,
    fetchResources,
    appliedSearchQuery,
    tagsQuery,
    excludeTagsQuery,
    characterIdsQuery,
    locationIdsQuery,
    showToast,
    showErrorToast,
  ]);

  const handleResourceTagsChange = React.useCallback(async (resourceId: string, nextTags: string[]): Promise<void> => {
    if (!projectId || !token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/resources/${resourceId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tags: nextTags }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as { error?: string }).error || 'Failed to update tags');
      }

      setResources((prev) => prev.map((r) => (r.id === resourceId ? { ...r, tags: nextTags } : r)));
      showFieldSavedToast(t('Tags'));
    } catch (err) {
      const message = err instanceof Error ? err.message : t('Failed to load resource');
      showErrorToast(message);
    }
  }, [projectId, token, t, showErrorToast, showFieldSavedToast]);

  const persistResourcePatch = React.useCallback(async (
    resourceId: string,
    body: { tags?: string[]; notes?: string | null }
  ): Promise<boolean> => {
    if (!projectId || !token) {
      return false;
    }
    const response = await fetch(`${API_BASE}/api/projects/${projectId}/resources/${resourceId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    return response.ok;
  }, [projectId, token]);

  const linkedEntityIdsForResource = React.useCallback((
    resourceId: string,
    entityType: 'character' | 'location',
    refs: typeof entityRefs
  ): string[] => {
    return refs
      .filter((ref) => ref.sourceType === 'resource' && ref.sourceId === resourceId && ref.targetType === entityType)
      .map((ref) => ref.targetId);
  }, []);

  const applyBulkAssignment = React.useCallback((params: {
    resourceIds: string[];
    addedCharacterIds?: string[];
    removedCharacterIds?: string[];
    addedLocationIds?: string[];
    removedLocationIds?: string[];
    addedTags?: string[];
    removedTags?: string[];
  }): void => {
    const run = async (): Promise<void> => {
      const addedCharacterIds = params.addedCharacterIds ?? [];
      const removedCharacterIds = params.removedCharacterIds ?? [];
      const addedLocationIds = params.addedLocationIds ?? [];
      const removedLocationIds = params.removedLocationIds ?? [];
      const addedTags = params.addedTags ?? [];
      const removedTags = params.removedTags ?? [];
      const assigningEntities =
        addedCharacterIds.length > 0 ||
        removedCharacterIds.length > 0 ||
        addedLocationIds.length > 0 ||
        removedLocationIds.length > 0;
      const assigningTags = addedTags.length > 0 || removedTags.length > 0;
      if ((!assigningEntities && !assigningTags) || params.resourceIds.length === 0) {
        return;
      }

      const characterOptionsById = new Map(characters.map((c) => [c.id, c]));
      const locationOptionsById = new Map(locations.map((l) => [l.id, l]));
      const resourceIdSet = new Set(params.resourceIds);
      const previousResources = resourcesRef.current;
      const previousRefs = entityRefsRef.current;
      const nextResources = previousResources.map((resource) => {
        if (!resourceIdSet.has(resource.id)) {
          return resource;
        }
        let next = resource;
        if (assigningTags) {
          next = { ...next, tags: applyTagAssignment(resource.tags || [], addedTags, removedTags) };
        }
        if (assigningEntities) {
          next = {
            ...next,
            notes: applyLinkedEntityAssignment({
              notes: resource.notes,
              currentCharacterIds: linkedEntityIdsForResource(resource.id, 'character', previousRefs),
              currentLocationIds: linkedEntityIdsForResource(resource.id, 'location', previousRefs),
              addedCharacterIds,
              removedCharacterIds,
              addedLocationIds,
              removedLocationIds,
              characterOptionsById,
              locationOptionsById,
            }),
          };
        }
        return next;
      });
      const nextRefs = previousRefs.filter((ref) => {
        if (ref.sourceType !== 'resource' || !resourceIdSet.has(ref.sourceId)) {
          return true;
        }
        if (ref.targetType === 'character' && removedCharacterIds.includes(ref.targetId)) {
          return false;
        }
        if (ref.targetType === 'location' && removedLocationIds.includes(ref.targetId)) {
          return false;
        }
        return true;
      });
      const extraRefs: typeof previousRefs = [];
      for (const resourceId of params.resourceIds) {
        for (const characterId of addedCharacterIds) {
          const exists = nextRefs.some(
            (ref) =>
              ref.sourceType === 'resource' &&
              ref.sourceId === resourceId &&
              ref.targetType === 'character' &&
              ref.targetId === characterId
          );
          if (!exists) {
            extraRefs.push({
              sourceType: 'resource',
              sourceId: resourceId,
              targetType: 'character',
              targetId: characterId,
            });
          }
        }
        for (const locationId of addedLocationIds) {
          const exists = nextRefs.some(
            (ref) =>
              ref.sourceType === 'resource' &&
              ref.sourceId === resourceId &&
              ref.targetType === 'location' &&
              ref.targetId === locationId
          );
          if (!exists) {
            extraRefs.push({
              sourceType: 'resource',
              sourceId: resourceId,
              targetType: 'location',
              targetId: locationId,
            });
          }
        }
      }

      resourcesRef.current = nextResources;
      entityRefsRef.current = [...nextRefs, ...extraRefs];
      setResources(nextResources);
      setEntityRefs(entityRefsRef.current);

      try {
        const results = await Promise.all(
          params.resourceIds.map(async (resourceId) => {
            const resource = nextResources.find((item) => item.id === resourceId);
            if (!resource) {
              return true;
            }
            const body: { tags?: string[]; notes?: string | null } = {};
            if (assigningTags) {
              body.tags = resource.tags;
            }
            if (assigningEntities) {
              body.notes = resource.notes;
            }
            return persistResourcePatch(resourceId, body);
          })
        );
        if (results.some((ok) => !ok)) {
          throw new Error('assign failed');
        }
        if (assigningEntities) {
          await fetchEntityRefs();
        }
      } catch (err) {
        resourcesRef.current = previousResources;
        entityRefsRef.current = previousRefs;
        setResources(previousResources);
        setEntityRefs(previousRefs);
        const message = err instanceof Error ? err.message : t('resources_assign_failed');
        showErrorToast(message === 'assign failed' ? t('resources_assign_failed') : message);
      }
    };

    assignQueueRef.current = assignQueueRef.current.then(run, run);
  }, [
    characters,
    locations,
    linkedEntityIdsForResource,
    persistResourcePatch,
    fetchEntityRefs,
    t,
    showErrorToast,
  ]);

  const columns = useMemo<ColumnDef<ResourceItem>[]>(
    () => {
      const cols: ColumnDef<ResourceItem>[] = [];
      if (canEditProject) {
        cols.push({
          id: 'select',
          header: ({ table }) => (
            <DataTableSelectAllCheckbox
              checked={
                table.getIsAllPageRowsSelected()
                  ? true
                  : table.getIsSomePageRowsSelected()
                    ? 'indeterminate'
                    : false
              }
              onCheckedChange={(checked) => {
                table.toggleAllPageRowsSelected(checked);
                const visibleIds = getVisibleRowIdsRef.current();
                setAnchorRowId(checked && visibleIds.length > 0 ? visibleIds[0] : null);
              }}
              label={t('resources_select_all')}
            />
          ),
          cell: ({ row }) => (
            <DataTableRowCheckbox
              checked={row.getIsSelected()}
              rowId={row.id}
              ariaLabel={t('resources_select_row')}
              onSelectRange={selectRangeTo}
              onToggle={toggleResourceSelected}
            />
          ),
          enableSorting: false,
          size: 40,
        });
      }
      cols.push(
        {
          accessorKey: 'title',
          header: ({ column }) => {
            return (
              <button
                onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted"
              >
                {t('Name')}
                {column.getIsSorted() === 'asc' ? (
                  <ArrowUp className="h-4 w-4" />
                ) : column.getIsSorted() === 'desc' ? (
                  <ArrowDown className="h-4 w-4" />
                ) : (
                  <ArrowUpDown className="h-4 w-4" />
                )}
              </button>
            );
          },
          cell: ({ row }) => {
            const urlValue = row.original.url;
            const title = row.original.title;
            const linkHostname = urlValue ? getUrlHostname(urlValue) : null;
            const TypeIcon = RESOURCE_KIND_ICONS[getResourceKind(row.original)];
            const subtitle = (
              <div className="flex flex-col text-xs text-muted-foreground">
                {linkHostname && <span className="truncate">{linkHostname}</span>}
                <span>{t('created')} {formatCreatedDate(row.original.createdAt)}</span>
                {row.original.resourceDate && (
                  <span>{t('reference_date_label')}: {formatReferenceDate(row.original.resourceDate)}</span>
                )}
              </div>
            );
            const mobileTags = (
              <div className="mt-1.5 min-[600px]:hidden" onClick={(e) => e.stopPropagation()}>
                <TagsCell
                  tags={row.original.tags || []}
                  entityTags={resourceEntityTags.get(row.original.id) ?? []}
                  availableTags={availableTags}
                  readOnly={!canEditProject}
                  onTagsChange={(nextTags) => handleResourceTagsChange(row.original.id, nextTags)}
                />
              </div>
            );
            if (urlValue) {
              return (
                <div className="flex items-start gap-2">
                  <TypeIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex min-w-0 flex-col">
                    <a
                      href={urlValue}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="block w-full break-words text-[0.95rem] font-medium text-scribane-green hover:text-foreground hover:font-semibold hover:no-underline"
                      title={urlValue}
                    >
                      {title}
                    </a>
                    {subtitle}
                    {mobileTags}
                  </div>
                </div>
              );
            }
            return (
              <div className="flex items-start gap-2">
                <TypeIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="flex min-w-0 flex-col">
                  <span className="break-words text-[0.95rem] font-medium">{title}</span>
                  {subtitle}
                  {mobileTags}
                </div>
              </div>
            );
          },
        },
        {
          accessorKey: 'tags',
          header: ({ column }) => {
            return (
              <button
                onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted"
              >
                {t('Tags')}
                {column.getIsSorted() === 'asc' ? (
                  <ArrowUp className="h-4 w-4" />
                ) : column.getIsSorted() === 'desc' ? (
                  <ArrowDown className="h-4 w-4" />
                ) : (
                  <ArrowUpDown className="h-4 w-4" />
                )}
              </button>
            );
          },
          cell: ({ row }) => {
            return (
              <TagsCell
                tags={row.original.tags || []}
                entityTags={resourceEntityTags.get(row.original.id) ?? []}
                availableTags={availableTags}
                readOnly={!canEditProject}
                onTagsChange={(nextTags) => handleResourceTagsChange(row.original.id, nextTags)}
              />
            );
          },
          sortingFn: (rowA, rowB) => {
            const tagsA = (rowA.original.tags || []).slice().sort().join(',');
            const tagsB = (rowB.original.tags || []).slice().sort().join(',');
            return tagsA.localeCompare(tagsB);
          },
        },
      );
      if (canEditProject) {
        cols.push({
          id: 'actions',
          header: () => <div></div>,
          cell: ({ row }) => {
            return (
              <div onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <EllipsisVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={(e) => requestDeleteResource(row.original.id, e)}
                      className="group text-red-600"
                    >
                      <RotatingDeleteIcon className="mr-2" />
                      {t('Delete resource')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          },
          enableSorting: false,
        });
      }
      return cols;
    },
      [
        t,
        requestDeleteResource,
        availableTags,
        resourceEntityTags,
        handleResourceTagsChange,
        canEditProject,
        selectRangeTo,
        toggleResourceSelected,
        setAnchorRowId,
      ]
  );

  const searchTextActive = appliedSearchQuery !== '';
  const isSearchRequestInFlight = loading && !initialLoading;

  const filteredResources = useMemo(() => {
    if (selectedResourceTypes.length === 0) {
      return resources.filter((resource) => getResourceKind(resource) !== 'document-image');
    }
    return resources.filter((resource) => selectedResourceTypes.includes(getResourceKind(resource)));
  }, [resources, selectedResourceTypes]);

  const sortedResources = useMemo(() => {
    const compareReferenceDates = (a: ResourceItem, b: ResourceItem, direction: 1 | -1): number => {
      if (!a.resourceDate && !b.resourceDate) {
        return 0;
      }
      if (!a.resourceDate) {
        return 1;
      }
      if (!b.resourceDate) {
        return -1;
      }
      return direction * a.resourceDate.localeCompare(b.resourceDate);
    };

    const items = [...filteredResources];
    items.sort((a, b) => {
      switch (sortOption) {
        case 'name_asc':
          return a.title.localeCompare(b.title);
        case 'created_asc':
          return a.createdAt.localeCompare(b.createdAt);
        case 'reference_date_desc':
          return compareReferenceDates(a, b, -1);
        case 'reference_date_asc':
          return compareReferenceDates(a, b, 1);
        case 'created_desc':
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filteredResources, sortOption]);

  const selectedResourceIdSet = useMemo(() => new Set(selectedResourceIds), [selectedResourceIds]);
  const hasResourceSelection = selectedResourceIds.length > 0;

  React.useEffect(() => {
    const visibleIds = new Set(sortedResources.map((resource) => resource.id));
    setRowSelection((prev) => {
      let changed = false;
      const next: RowSelectionState = {};
      for (const [id, selected] of Object.entries(prev)) {
        if (selected && visibleIds.has(id)) {
          next[id] = true;
        } else if (selected) {
          changed = true;
        }
      }
      if (!changed && Object.keys(next).length === Object.keys(prev).length) {
        return prev;
      }
      return changed ? next : prev;
    });
  }, [sortedResources, setRowSelection]);

  React.useEffect(() => {
    if (!hasResourceSelection) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        clearSelection();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hasResourceSelection, clearSelection]);

  const selectedResourceEntityIds = useMemo(() => {
    return selectedResourceIds.map((resourceId) => {
      const entityTags = resourceEntityTags.get(resourceId) ?? [];
      return {
        characterIds: entityTags.filter((tag) => tag.type === 'character').map((tag) => tag.id),
        locationIds: entityTags.filter((tag) => tag.type === 'location').map((tag) => tag.id),
        tagIds: (resources.find((resource) => resource.id === resourceId)?.tags ?? [])
          .map(normalizeTag)
          .filter((tag) => tag && !isSystemTag(tag)),
      };
    });
  }, [selectedResourceIds, resourceEntityTags, resources, normalizeTag]);

  const assignmentCharacterIds = useMemo(
    () => (hasResourceSelection ? intersectIds(selectedResourceEntityIds.map((item) => item.characterIds)) : selectedCharacterIds),
    [hasResourceSelection, selectedResourceEntityIds, selectedCharacterIds]
  );
  const assignmentLocationIds = useMemo(
    () => (hasResourceSelection ? intersectIds(selectedResourceEntityIds.map((item) => item.locationIds)) : selectedLocationIds),
    [hasResourceSelection, selectedResourceEntityIds, selectedLocationIds]
  );
  const assignmentTagIds = useMemo(
    () => (hasResourceSelection ? intersectIds(selectedResourceEntityIds.map((item) => item.tagIds)) : selectedTags.map(normalizeTag)),
    [hasResourceSelection, selectedResourceEntityIds, selectedTags, normalizeTag]
  );

  const handleCharacterIdsChange = React.useCallback((nextIds: string[]): void => {
    if (!hasResourceSelection) {
      setSelectedCharacterIds(nextIds);
      return;
    }
    const { added, removed } = diffIds(assignmentCharacterIds, nextIds);
    applyBulkAssignment({
      resourceIds: selectedResourceIds,
      addedCharacterIds: added,
      removedCharacterIds: removed,
    });
  }, [hasResourceSelection, assignmentCharacterIds, applyBulkAssignment, selectedResourceIds]);

  const handleLocationIdsChange = React.useCallback((nextIds: string[]): void => {
    if (!hasResourceSelection) {
      setSelectedLocationIds(nextIds);
      return;
    }
    const { added, removed } = diffIds(assignmentLocationIds, nextIds);
    applyBulkAssignment({
      resourceIds: selectedResourceIds,
      addedLocationIds: added,
      removedLocationIds: removed,
    });
  }, [hasResourceSelection, assignmentLocationIds, applyBulkAssignment, selectedResourceIds]);

  const handleTagIdsChange = React.useCallback((nextIds: string[]): void => {
    if (!hasResourceSelection) {
      setSelectedTags(nextIds);
      return;
    }
    const { added, removed } = diffIds(assignmentTagIds, nextIds);
    applyBulkAssignment({
      resourceIds: selectedResourceIds,
      addedTags: added,
      removedTags: removed,
    });
  }, [hasResourceSelection, assignmentTagIds, applyBulkAssignment, selectedResourceIds]);

  const handleSelectGridResource = React.useCallback((id: string, e: React.MouseEvent): void => {
    handleModifierAwareNavigate(e, navigate, `/projects/${projectId}/resources/${id}`);
  }, [navigate, projectId]);

  const assignModeClassName = hasResourceSelection
    ? 'border-[var(--scribane-green)] bg-[rgba(94,204,128,0.08)]'
    : undefined;

  const resourceTypeOptions = useMemo(
    () => [
      { id: 'text', name: t('resource_type_text') },
      { id: 'link', name: t('resource_type_link') },
      { id: 'image', name: t('resource_type_image') },
      { id: 'document-image', name: t('resource_type_document_image') },
      { id: 'document', name: t('resource_type_document') },
    ],
    [t]
  );

  const typeFilterOptions = useMemo(() => {
    const presentKinds = presentResourceKindIds(resources, selectedResourceTypes);
    return resourceTypeOptions.filter((option) => presentKinds.has(option.id));
  }, [resourceTypeOptions, resources, selectedResourceTypes]);

  const characterFilterOptions = useMemo(
    () => resourceFilterOptions({
      allOptions: characters,
      usedIds: linkedResourceEntityIds(entityRefs, 'character'),
      selectedIds: selectedCharacterIds,
      showAll: hasResourceSelection,
    }),
    [characters, entityRefs, selectedCharacterIds, hasResourceSelection]
  );

  const locationFilterOptions = useMemo(
    () => resourceFilterOptions({
      allOptions: locations,
      usedIds: linkedResourceEntityIds(entityRefs, 'location'),
      selectedIds: selectedLocationIds,
      showAll: hasResourceSelection,
    }),
    [locations, entityRefs, selectedLocationIds, hasResourceSelection]
  );

  const tagFilterOptions = useMemo(() => {
    const tagsOnResources = nonSystemTagsFromResources(resources);
    const tagNames = hasResourceSelection
      ? mergeTagNames(projectTags, tagsOnResources, assignmentTagIds)
      : mergeTagNames(tagsOnResources, selectedTags, excludedTags);
    return tagNames.map((tag) => ({ id: tag, name: tag }));
  }, [hasResourceSelection, projectTags, resources, assignmentTagIds, selectedTags, excludedTags]);

  const mobileFilterEntries: ResourcesFilterMenuEntry[] = useMemo(() => {
    const entries: ResourcesFilterMenuEntry[] = [];
    if (typeFilterOptions.length > 0) {
      entries.push({
        key: 'type',
        label: t('filter_type'),
        icon: FileType,
        options: typeFilterOptions,
        selectedIds: selectedResourceTypes,
        onChange: setSelectedResourceTypes,
      });
    }
    if (characterFilterOptions.length > 0) {
      entries.push({
        key: 'characters',
        label: hasResourceSelection ? t('assign_characters') : t('filter_characters'),
        icon: CONCEPT_ICONS.character,
        options: characterFilterOptions,
        selectedIds: assignmentCharacterIds,
        onChange: handleCharacterIdsChange,
        highlight: hasResourceSelection,
      });
    }
    if (locationFilterOptions.length > 0) {
      entries.push({
        key: 'locations',
        label: hasResourceSelection ? t('assign_locations') : t('filter_locations'),
        icon: CONCEPT_ICONS.location,
        options: locationFilterOptions,
        selectedIds: assignmentLocationIds,
        onChange: handleLocationIdsChange,
        highlight: hasResourceSelection,
      });
    }
    if (tagFilterOptions.length > 0 || hasResourceSelection) {
      entries.push({
        key: 'tags',
        label: hasResourceSelection ? t('assign_tags') : t('filter_tags'),
        icon: CONCEPT_ICONS.tag,
        options: tagFilterOptions,
        selectedIds: assignmentTagIds,
        onChange: handleTagIdsChange,
        excludedIds: hasResourceSelection ? undefined : excludedTags,
        onExcludedChange: hasResourceSelection ? undefined : setExcludedTags,
        allowExclude: !hasResourceSelection,
        highlight: hasResourceSelection,
        createLabel: hasResourceSelection ? (tag) => `${t('Create tag')} ${tag}` : undefined,
      });
    }
    return entries;
  }, [
    t,
    typeFilterOptions,
    selectedResourceTypes,
    characterFilterOptions,
    assignmentCharacterIds,
    handleCharacterIdsChange,
    locationFilterOptions,
    assignmentLocationIds,
    handleLocationIdsChange,
    tagFilterOptions,
    assignmentTagIds,
    handleTagIdsChange,
    excludedTags,
    hasResourceSelection,
  ]);

  const table = useReactTable({
    data: sortedResources,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: canEditProject,
    getRowId: (row) => row.id,
    state: {
      sorting,
      rowSelection,
    },
  });

  getVisibleRowIdsRef.current = (): string[] =>
    table.getRowModel().rows.map((row) => row.id);

  const hasActiveFilters = Boolean(
    searchTextActive ||
    selectedTags.length > 0 ||
    excludedTags.length > 0 ||
    selectedCharacterIds.length > 0 ||
    selectedLocationIds.length > 0 ||
    selectedResourceTypes.length > 0
  );

  const clearFilters = React.useCallback((): void => {
    setAppliedSearchQuery('');
    setSearchInputResetKey((key) => key + 1);
    setSelectedTags([]);
    setExcludedTags([]);
    setSelectedCharacterIds([]);
    setSelectedLocationIds([]);
    setSelectedResourceTypes([]);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('tags');
        next.delete('excludeTags');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const isTrulyEmpty = !loading && !initialLoading && resources.length === 0 && !hasActiveFilters;

  const resourcesActions = projectId && canEditProject && shouldShowHeaderCreateActions(!isTrulyEmpty, isMobile) ? (
    <AddResourceMenu
      size="sm"
      variant="inverse"
      onAddText={() => setIsAddGenericResourceModalOpen(true)}
      onAddLink={() => setIsAddLinkModalOpen(true)}
      onAddDocument={() => setIsUploadModalOpen(true)}
    />
  ) : undefined;

  if (initialLoading) {
    return (
      <ProjectPanelLayout title={t('Resources')} titleIcon={PROJECT_SECTION_ICONS.resources} actions={resourcesActions} loading />
    );
  }

  if (isTrulyEmpty) {
    return (
      <ProjectPanelLayout title={t('Resources')} titleIcon={PROJECT_SECTION_ICONS.resources} actions={resourcesActions}>
        <ResourcesEmptyState
          canEdit={Boolean(canEditProject)}
          onAddText={() => setIsAddGenericResourceModalOpen(true)}
          onAddLink={() => setIsAddLinkModalOpen(true)}
          onAddDocument={() => setIsUploadModalOpen(true)}
        />
        {projectId && (
          <UploadResourcesModal
            isOpen={isUploadModalOpen}
            onClose={() => setIsUploadModalOpen(false)}
            projectId={projectId}
          />
        )}
        {projectId && (
          <AddGenericResourceModal
            isOpen={isAddGenericResourceModalOpen}
            onClose={() => setIsAddGenericResourceModalOpen(false)}
            projectId={projectId}
          />
        )}
        {projectId && (
          <AddLinkModal
            isOpen={isAddLinkModalOpen}
            onClose={() => setIsAddLinkModalOpen(false)}
            projectId={projectId}
          />
        )}
      </ProjectPanelLayout>
    );
  }

  return (
    <>
      <ProjectPanelLayout title={t('Resources')} titleIcon={PROJECT_SECTION_ICONS.resources} actions={resourcesActions}>
        <Tabs
          value={viewMode}
          onValueChange={(v) => setViewMode(v as 'table' | 'grid')}
          className="flex min-h-0 min-w-0 flex-1 flex-col"
        >
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, flex: 1 }}>
          <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%', flexWrap: 'wrap' }}>
              <ResourcesSearchInput
                key={`${projectId}-${searchInputResetKey}`}
                isFetching={isSearchRequestInFlight}
                onDebouncedChange={handleDebouncedSearch}
              />
              <div className="hidden items-center gap-4 xl:flex">
                {typeFilterOptions.length > 0 && (
                  <MultiSelectFilterDropdown
                    label={t('filter_type')}
                    icon={FileType}
                    options={typeFilterOptions}
                    selectedIds={selectedResourceTypes}
                    onChange={setSelectedResourceTypes}
                  />
                )}
                {characterFilterOptions.length > 0 && (
                  <MultiSelectFilterDropdown
                    label={hasResourceSelection ? t('assign_characters') : t('filter_characters')}
                    icon={CONCEPT_ICONS.character}
                    options={characterFilterOptions}
                    selectedIds={assignmentCharacterIds}
                    onChange={handleCharacterIdsChange}
                    className={assignModeClassName}
                  />
                )}
                {locationFilterOptions.length > 0 && (
                  <MultiSelectFilterDropdown
                    label={hasResourceSelection ? t('assign_locations') : t('filter_locations')}
                    icon={CONCEPT_ICONS.location}
                    options={locationFilterOptions}
                    selectedIds={assignmentLocationIds}
                    onChange={handleLocationIdsChange}
                    className={assignModeClassName}
                  />
                )}
                {(tagFilterOptions.length > 0 || hasResourceSelection) && (
                  <MultiSelectFilterDropdown
                    label={hasResourceSelection ? t('assign_tags') : t('filter_tags')}
                    icon={CONCEPT_ICONS.tag}
                    options={tagFilterOptions}
                    selectedIds={assignmentTagIds}
                    onChange={handleTagIdsChange}
                    excludedIds={hasResourceSelection ? undefined : excludedTags}
                    onExcludedChange={hasResourceSelection ? undefined : setExcludedTags}
                    allowExclude={!hasResourceSelection}
                    className={assignModeClassName}
                    createLabel={hasResourceSelection ? (tag) => `${t('Create tag')} ${tag}` : undefined}
                  />
                )}
              </div>
              <div className="flex xl:hidden">
                <ResourcesFilterMenu filters={mobileFilterEntries} assignMode={hasResourceSelection} />
              </div>
              <ClearFiltersButton enabled={hasActiveFilters} onClick={clearFilters} />
              {canEditProject && viewMode === 'grid' && (
                <DataTableSelectAllCheckbox
                  checked={
                    sortedResources.length > 0 && sortedResources.every((resource) => selectedResourceIdSet.has(resource.id))
                      ? true
                      : sortedResources.some((resource) => selectedResourceIdSet.has(resource.id))
                        ? 'indeterminate'
                        : false
                  }
                  onCheckedChange={(checked) => {
                    if (checked) {
                      const next: RowSelectionState = {};
                      for (const resource of sortedResources) {
                        next[resource.id] = true;
                      }
                      setRowSelection(next);
                      setAnchorRowId(sortedResources[0]?.id ?? null);
                    } else {
                      clearSelection();
                    }
                  }}
                  label={t('resources_select_all')}
                />
              )}
              <DataTableSelectionActions
                selectedCount={selectedResourceIds.length}
                countLabel={t('resources_selected_count').replace(
                  '{count}',
                  String(selectedResourceIds.length)
                )}
                onClear={clearSelection}
                onDelete={canEditProject ? requestDeleteSelectedResources : undefined}
                deleting={bulkDeleting}
              />
              <div className="ml-auto flex shrink-0 items-center gap-4">
                <ResourcesSortMenu value={sortOption} onChange={setSortOption} />
                <ViewSwitcher
                  modes={['table', 'grid']}
                  ariaLabelForMode={(mode) =>
                    mode === 'table' ? t('resources_view_table') : t('resources_view_grid')
                  }
                />
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, flex: 1, overflow: 'hidden' }}>
          {viewMode === 'table' ? (
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: '0.5rem',
              overflow: 'auto',
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'var(--card)',
            }}
          >
            <Table style={{ width: '100%', height: '100%', tableLayout: 'fixed' }}>
              <colgroup>
                {canEditProject && <col className="w-10" />}
                <col className="w-full min-[600px]:w-[40%]" />
                <col className="hidden min-[600px]:table-column min-[600px]:w-[60%]" />
                {canEditProject && <col className="w-14" />}
              </colgroup>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className={cn(
                          header.column.id === 'select' && 'w-10 pl-4 pr-0',
                          header.column.id === 'title' && (canEditProject ? 'min-w-[350px]' : 'min-w-[350px] pl-6'),
                          header.column.id === 'tags' && 'hidden min-[600px]:table-cell'
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem' }}>
                      {t('resources_empty_no_results')}
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => {
                    const resourcePath = `/projects/${projectId}/resources/${row.original.id}`;
                    const navigationHandlers = navigationClickHandlers(navigate, resourcePath);
                    return (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() ? 'selected' : undefined}
                      onClick={(event) => {
                        if (canEditProject) {
                          handleRowClick(row.id, event, () => navigationHandlers.onClick(event));
                          return;
                        }
                        navigationHandlers.onClick(event);
                      }}
                      onAuxClick={navigationHandlers.onAuxClick}
                      className={cn(
                        'cursor-pointer transition-colors hover:bg-muted',
                        row.getIsSelected() && 'bg-muted'
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            'py-4',
                            cell.column.id === 'select' && 'w-10 pl-4 pr-0',
                            cell.column.id === 'title' && (canEditProject ? 'min-w-[350px]' : 'min-w-[350px] pl-6'),
                            cell.column.id === 'tags' && 'hidden min-[600px]:table-cell'
                          )}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          ) : (
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: '0.5rem',
                overflow: 'auto',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {projectId && token ? (
                <ResourcesGrid
                  resources={sortedResources}
                  projectId={projectId}
                  token={token}
                  resourceEntityTags={resourceEntityTags}
                  onSelectResource={handleSelectGridResource}
                  emptyLabel={t('resources_empty_no_results')}
                  selectionEnabled={canEditProject}
                  selectedResourceIds={selectedResourceIdSet}
                  onToggleResourceSelected={toggleResourceSelected}
                />
              ) : (
                <div className="flex flex-1 items-center justify-center p-8 text-muted-foreground">
                  {t('loading')}
                </div>
              )}
            </div>
          )}
          </div>
        </div>
        </Tabs>
      </ProjectPanelLayout>
      {projectId && (
        <UploadResourcesModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          projectId={projectId}
        />
      )}
      {projectId && (
        <AddGenericResourceModal
          isOpen={isAddGenericResourceModalOpen}
          onClose={() => setIsAddGenericResourceModalOpen(false)}
          projectId={projectId}
        />
      )}
      {projectId && (
        <AddLinkModal
          isOpen={isAddLinkModalOpen}
          onClose={() => setIsAddLinkModalOpen(false)}
          projectId={projectId}
        />
      )}
      <ConfirmDialog
        open={pendingDeleteResourceIds.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDeleteResourceIds([]);
          }
        }}
        title={
          pendingDeleteResourceIds.length === 1
            ? t('Delete resource')
            : t('delete_resources')
        }
        description={
          pendingDeleteResourceIds.length === 1
            ? t('Are you sure you want to delete this resource?')
            : t('confirm_delete_resources').replace(
                '{count}',
                String(pendingDeleteResourceIds.length)
              )
        }
        confirmLabel={
          pendingDeleteResourceIds.length === 1 ? t('Delete resource') : t('delete')
        }
        onConfirm={() => {
          void confirmDeleteResources();
        }}
      />
    </>
  );
};
